import {Controller} from "stimulus";

/*
  Add this controller to a form and allow various elements within the form to be
  hidden/shown based on other values within the form.  This allows more complex
  requirements than dependent-form-section, such as the ability to have multiple
  nested sections, as well as more complex lookups.

  Configure the controller with the following values:

  - data-conditional-form-prefix-value sets the prefix, e.g. for a form with
    fields like "form[field1]" the prefix will be "form"

  - data-conditional-form-hidden-inputs-behaviour-value sets how hidden inputs
    are treated.  Options are "clear" (default) which will clear the values of
    hidden inputs, or "disable" which will disable the inputs. Disabled inputs
    are not normally submitted by browsers whereas cleared inputs will be
    submitted as empty values.

  To have the controller add a class to hidden sections, set the
  data-conditional-form-hidden-class attribute to the desired class name.

  Sections have the following attributes:

  - data-conditional-form-target="section": marks the element as a section that
    can be toggled.

  - data-conditional-form-attribute: the attribute to watch, e.g. "field1" for a
    field with name "form[field1]".

    Nested attributes can be referenced using dot notation, e.g. to reference
    the field "form[foo][bar]"", you would use
    data-conditional-form-attribute="foo.bar" on the target section.

  - data-conditional-form-value: the value to match against.  This can be a
    single value, or a JSON array of values if using the "contains" operator.

  - data-conditional-form-operator: the operator to use when matching.  Options
    are:

    - "default" (or omitted): matches if the attribute value equals the provided
      value.
    - "contains": matches if the attribute value is contained within the
      provided JSON array of values.
    - "any": matches if the attribute value is not empty.

  - data-scroll-into-view="true": (optional) if set to true, when this section
    becomes visible it will be scrolled into view. Note: Only do when event
    causes calculation (ie. not initialize) and previously hidden (ie. now
    showing)
*/
export default class extends Controller<HTMLElement> {
  static values = {
    prefix: String,

    // We can *clear* inputs hidden within a section of *disable* those inputs.
    // Both have pros and cons given the circumstances
    hiddenInputsBehaviour: String,
  };

  static targets = ["section"];
  static classes = ["hidden"];

  declare prefixValue: string;
  declare hiddenInputsBehaviourValue: "clear" | "disable";
  declare sectionTargets: HTMLElement[];
  declare toggleHiddenInputs: (showing: boolean) => (input: HTMLInputElement) => void;
  declare hiddenClass: string;
  declare hasHiddenClass: boolean;

  // Calculate each section's visibility as the controller is initialized,
  // and then listen to any form changes to trigger a re-calculation. Note
  // that you will need to call calculate programmatically if input values
  // are changed programmatically e.g. a hidden input is updated.
  initialize() {
    // Configure the behaviour for nested inputs when shown/hidden
    this.toggleHiddenInputs = this.toggleClearInputs;
    if (this.hiddenInputsBehaviourValue === "disable") {
      this.toggleHiddenInputs = this.toggleDisableInputs;
    }

    this.calculate();
    this.element.addEventListener("input", (event) => this.calculate(event));
  }

  calculate(event?: any) {
    this.sectionTargets.forEach(section => {
      const attribute = section.dataset.conditionalFormAttribute as string;
      const value = section.dataset.conditionalFormValue as string;
      const operator = section.dataset.conditionalFormOperator as string;
      const currentValue = this.getFormValue(attribute);

      // A section can be shown/hidden based on different operators
      // - contains: does the value match one of the provided values
      // - any: simply is the value of the input not empty
      // - default: does the value equal the provided value
      const isAMatch = () => {
        switch (operator) {
        case "contains":
          return JSON.parse(value).includes(currentValue);
        case "any":
          return !!currentValue;
        default:
          return currentValue === value;
        }
      };

      const matched = isAMatch();
      if (matched) {
        const previousDisplay = section.style.display;
        section.style.display = "block";
        if (this.hasHiddenClass) {
          section.classList.remove(this.hiddenClass);
        }

        // Conditional elements may reveal outside viewport (and be nice to scroll to)
        // @note Only do when event causes calculation (ie. not initialize) and previously hidden (ie. now showing)
        if (event !== undefined && previousDisplay === "none" && section.dataset.scrollIntoView == "true") {
          section.scrollIntoView({behavior: "smooth"});
        }
        this.dispatch("visibilityToggled", {detail: {visible: true, element: section}});
      } else {
        section.style.display = "none";
        if (this.hasHiddenClass) {
          section.classList.add(this.hiddenClass);
        }
        this.dispatch("visibilityToggled", {detail: {visible: false, element: section}});
      }

      // We also want to clear any values from any hidden sections.
      const sectionInputTreatment = this.toggleHiddenInputs(matched);
      section.querySelectorAll("input, select, textarea").forEach(sectionInputTreatment);
    });
  }

  // Get the value for a certain input by getting all of
  // the form data, and extracting the desired attribute.
  // e.g. incident_categorisation[primary_category]
  getFormValue(attribute: string) {
    const formElement = this.element.tagName == "FORM"
      ? this.element as HTMLFormElement
      : this.element.closest("form")!;

    const deepAttribute = attribute.split(".").map(part => `[${part}]`).join("");

    const formData = new FormData(formElement);

    // Rails uses hidden default values behind checkbox controls
    // using the last value from `getAll` works for single attributes
    // as well as these checkboxes
    return formData.getAll(`${this.prefixValue}${deepAttribute}`).pop();
  }

  // This was stripped from the addable-nested-attributes controller.
  toggleClearInputs = (showing: boolean) => (input: HTMLInputElement) => {
    if (showing) return; // We don't have a show action, we can't undo this

    const isBoolean = ["checkbox", "radio"].includes(input.type);
    const isSelect = input.tagName === "SELECT";

    if (isBoolean) {
      input.checked = false;
    } else if (isSelect) {
      input.value = input.querySelector("option")?.value as string;
    } else {
      input.value = "";
    }
  };

  // Toggle hidden section inputs
  toggleDisableInputs = (showing: boolean) => (input: HTMLInputElement) => {
    input.disabled = !showing;
  };
}
